import Utilities from './lib/utilities.js';
import Route from "./lib/route";
import Stop from "./lib/stop";
import Markers from "./lib/markers";
import Agency from "./lib/agency";
import Directions from "./lib/directions";
import Print from "./lib/print";
import History from "./lib/history";

import 'what-input';

window.TransitMap = (() => {
	'use strict';

	const tm = {};

	const constructor = () => {
		tm.$map = document.getElementById( 'tm__map' );
		if ( !tm.$map ) return;

		tm.visible = {};
		tm.$sidebar = document.getElementById( 'tm__sidebar' );

		// the routes/tripplanner/alerts tabs
		initSidebarTabs();

		// the Google map and all dependent functionality
		initTransitMap();

		// on mobile, allows users to drag a grabber bar to resize the map
		initMobileGrabber();

		// Maybe change the active tab
		History.init( tm );

		// Alerts functionality
		initAlerts();
	}

	const initTransitMap = async () => {
		const {Map} = await google.maps.importLibrary( 'maps' );

		tm.stopLayers = {};
		tm.map = new Map( tm.$map, {
			zoom: 9,
			center: {lat: 45.22085356509325, lng: - 122.6934966796875},
			clickableIcons: false,
			disableDefaultUI: true,
			mapId: tmVars.mapId
		} );

		// Init the rest of the plugin functionality (e.g. anything that depends on having a loaded Google map)
		google.maps.event.addListenerOnce( tm.map, 'idle', initGmapsFunctionality );
	}

	const initGmapsFunctionality = async () => {
		const functionPromises = [
			await Utilities.initMapIdle( tm ),
			await Utilities.shouldDisplayZoomControls( tm ),
			await Markers.init( tm ),
			await Route.init( tm ),
			await Stop.init( tm ),
			await Directions.init( tm ),
			await Print.init( tm )
		];

		await Promise.all( functionPromises );

		// panel management
		tm.$sidebar.addEventListener( 'tm__change-panel', ( e ) => initChangePanelListener( e ) );
		initSidebarPanels();
		await History.triggerPanelClickEventOnLoad( tm );

		// indicate the map is loaded, via both an event and a data attribute
		tm.$map.dataset.loaded = 'true';
		let event = new CustomEvent( 'tm__gmaps-loaded' );
		window.dispatchEvent( event );
	}

	const initChangePanelListener = async ( e ) => {
		let pane = e.detail.pane.split( '|' ),
			paneId = parseInt( pane[1], 10 ),
			paneName = pane[2];
		switch ( e.detail.panel ) {
			case "overview_bus_routes":
				openPanelBusRoutesOverview();
				break;
			case "agency_detail":
				Agency.openPanelAgencyDetail( tm, paneId );
				break;
			case "route_detail":
				Route.openPanelRouteDetail( tm, paneId, paneName );
				break;
			case "stop_detail":
				await Stop.openPanelStopDetail( tm, paneId, paneName );
				break;
		}

		const agencyColor = Utilities.getStrokeColor( 'agency_' + tm.visible.agencyId );
		tm.$sidebar.style.setProperty( '--agency-color', agencyColor );

		History.maybePushPanelState( tm, e.detail );
	}

	const openPanelBusRoutesOverview = () => {
		Agency.updateVisibleAgency( tm, null );
		Route.updateVisibleRoute( tm, null );
		Stop.updateVisibleStop( tm, null );
		Stop.revertStopStyling( tm );
		Stop.closeStopInfowindow();
		Agency.showVisibleAgency( tm );
	}

	const initMobileGrabber = () => {
		const grabber = document.getElementById( 'tm__grabber' ),
			getEventY = ( e ) => {
				// different for mouse vs. touchscreen
				return e.clientY || e.targetTouches[0].pageY;
			},
			resize = ( e ) => {
				e.preventDefault();
				const height = mapHeight + getEventY( e ) - startY;
				tm.$map.style.setProperty( '--custom-height', height + 'px' );
			},
			throttledResize = Utilities.throttle( resize, 50 ),
			stopResize = () => {
				document.documentElement.removeEventListener( 'mousemove', throttledResize );
				document.documentElement.removeEventListener( 'touchmove', throttledResize );
				document.documentElement.removeEventListener( 'mouseup', stopResize );
				document.documentElement.removeEventListener( 'touchend', stopResize );
			},
			startResize = ( e ) => {
				e.preventDefault();
				startY = getEventY( e )
				mapHeight = parseInt( document.defaultView.getComputedStyle( tm.$map ).height, 10 );
				document.documentElement.addEventListener( 'mousemove', throttledResize );
				document.documentElement.addEventListener( 'touchmove', throttledResize );
				document.documentElement.addEventListener( 'mouseup', stopResize );
				document.documentElement.addEventListener( 'touchend', stopResize );
			};

		let startY, mapHeight;
		grabber.addEventListener( 'mousedown', startResize );
		grabber.addEventListener( 'touchstart', startResize, {passive: true} );
	}

	const initAlerts = () => {
		const $buttons = document.getElementById( 'tab-alerts' ).querySelectorAll( '[data-open-section]' );
		$buttons.forEach( ( $button ) => {
			$button.addEventListener( 'click', () => {
				// update the agency ID
				const agencyId = parseInt( $button.dataset.agency, 10 );
				Agency.updateVisibleAgency( tm, agencyId );

				// change from Alerts tab to Bus Routes
				const event = new CustomEvent( 'tm__change-tab', {detail: {oldIndex: 2, newIndex: 0, dontPushHistory: true}} );
				tm.$sidebar.dispatchEvent( event );
			}, true );
		} );
	}

	const initSidebarPanels = () => {
		const panelButtonClicked = ( detail ) => {
			const maybeDoTransition = () => {
				// don't transition if explicitly disabled
				if ( detail.transition === false ) return;

				// don't transition if opening the same panel
				if ( prevPanel === newPanel ) return;

				if ( !$panel ) return;

				// don't transition if opening a non-adjacent panel
				if ( Math.abs( prevPanel - newPanel ) > 1 ) {
					tm.$sidebar.style.setProperty( "--transition-duration", "0s" );
					setTimeout( () => {
						tm.$sidebar.style.removeProperty( "--transition-duration" );
					}, 500 ); // TODO shouldn't be hardcoded
					return;
				}

				let direction = prevPanel > newPanel ? 'right' : 'left';
				$section.classList.add( "tm__transition--" + direction );
				$panel.addEventListener( "transitionend", () => {
					$section.classList.remove( "tm__transition--" + direction );
				}, {once: true} );
			}

			if ( !detail.section || !detail.panel ) return;

			const $section = document.getElementById( detail.section );
			if ( !$section ) {
				console.warn( "no element with id", detail.section );
				return;
			}
			let $panel, prevPanel, newPanel;

			[...$section.children].filter( ( el, i ) => {
				if ( el.getAttribute( "aria-hidden" ) === 'false' ) prevPanel = i;

				if ( el.matches( '[data-panel="' + detail.panel + '"]' ) ) {
					el.setAttribute( 'aria-hidden', 'false' );
					el.removeAttribute( 'inert' );
					$panel = el;
					newPanel = i;
				} else {
					el.setAttribute( 'aria-hidden', 'true' );
					el.setAttribute( 'inert', '' );
				}
			} );

			// if a matching pane exists, show it; otherwise don't change the visible pane
			if ( detail.pane && $panel ) {
				const $panes = $panel.querySelectorAll( '[data-pane]' ),
					pane = [...$panes].find( el => {
						return el.matches( '[data-pane="' + detail.pane + '"]' );
					} );
				if ( pane ) {
					[...$panes].filter( el => {
						if ( el === pane ) {
							el.setAttribute( 'aria-hidden', 'false' );
							el.removeAttribute( 'inert' );
						} else {
							el.setAttribute( 'aria-hidden', 'true' );
							el.setAttribute( 'inert', '' );
						}
					} );
				}
			}

			maybeDoTransition();

			// trigger an event for the Google map functionality to hook into
			let event = new CustomEvent( 'tm__change-panel', {detail: {panel: detail.panel, pane: detail.pane, dontPushHistory: detail.dontPushHistory}} );
			tm.$sidebar.dispatchEvent( event );
		}

		// buttons that already exist on pageload
		const $panelButtons = tm.$sidebar.querySelectorAll( '[data-open-panel]' )
		$panelButtons.forEach( ( $button ) => {
			const panel = $button.dataset.openPanel;
			let section = $button.closest( 'section' ).id;
			$button.addEventListener( 'click', ( e ) => {
				section = $button.dataset.openSection ? $button.dataset.openSection : section;
				const pane = $button.dataset.openPane,
					transition = !$button.dataset.transition;
				panelButtonClicked( {section, panel, pane, transition} );
			} );
		} );

		// dynamically added buttons that open stop details
		const $stopButtonWrapper = document.getElementById( 'tm__route-detail' );
		$stopButtonWrapper.addEventListener( 'click', ( e ) => {
			if ( e.target.closest( 'button' ) ) {
				const section = $stopButtonWrapper.closest( 'section' ).id,
					panel = 'stop_detail',
					pane = e.target.dataset.openPane + '|' + e.target.textContent; // include the stop name
				panelButtonClicked( {section, panel, pane} );
			}
		} );

		// allow flexible triggering using an event
		tm.$sidebar.addEventListener( 'tm__panel-button-clicked', ( e ) => {
			panelButtonClicked( e.detail );
		} );
	}

	const initSidebarTabs = () => {
		// Modified from https://inclusive-components.design/tm.$sidebar-interfaces/

		const tablist = tm.$sidebar.querySelector( 'ul' ),
			tabs = tablist.querySelectorAll( 'a' ),
			panels = tm.$sidebar.querySelectorAll( '[id^="tab"]' );

		tm.$sidebar.addEventListener( 'tm__change-tab', ( e ) => {
			let [newTab, oldTab, newIndex, oldIndex] = Utilities.getMissingTabsOrIndices( tm, e.detail, tabs );
			if ( !newTab || !oldTab || typeof newIndex === 'undefined' || typeof oldIndex === 'undefined' ) return;

			// Update the tabs
			oldTab.setAttribute( 'tabindex', '-1' );
			oldTab.removeAttribute( 'aria-selected' );
			newTab.removeAttribute( 'tabindex' );
			newTab.setAttribute( 'aria-selected', 'true' );
			newTab.focus();

			// Update the panels
			panels[oldIndex].setAttribute( 'aria-hidden', 'true' );
			panels[oldIndex].setAttribute( 'inert', '' );
			panels[newIndex].setAttribute( 'aria-hidden', 'false' );
			panels[newIndex].removeAttribute( 'inert' );

			// Push to history, if not going back
			History.maybePushTabState( tm, e, newTab );
		} );

		// Initialize tabs
		Array.prototype.forEach.call( tabs, ( tab, i ) => {
			tab.setAttribute( 'role', 'tab' );
			tab.setAttribute( 'id', 'tab' + i );
			tab.parentNode.setAttribute( 'role', 'presentation' );

			if ( i === 0 ) {
				tab.setAttribute( 'aria-selected', 'true' );
			} else {
				tab.setAttribute( 'tabindex', '-1' );
			}

			// Handle clicking of tabs for mouse users
			tab.addEventListener( 'click', e => {
				e.preventDefault();
				e.stopPropagation();

				const currentTab = tablist.querySelector( '[aria-selected]' ),
					oldIndex = Array.prototype.indexOf.call( tabs, currentTab ),
					newIndex = Array.prototype.indexOf.call( tabs, e.currentTarget );

				if ( e.currentTarget !== currentTab ) {
					const event = new CustomEvent( 'tm__change-tab', {detail: {oldIndex: oldIndex, newIndex: newIndex}} );
					tm.$sidebar.dispatchEvent( event );
				} else {
					if ( oldIndex === 0 ) {
						// clicked on Bus Routes and was already on Bus Routes: open agency overview pane
						const $stopButtonWrapper = document.getElementById( 'tm__route-detail' ),
							section = $stopButtonWrapper.closest( 'section' ).id,
							event = new CustomEvent( 'tm__panel-button-clicked', {detail: {section: section, panel: 'overview_bus_routes', pane: 'overview_bus_routes'}} );
						tm.$sidebar.dispatchEvent( event );
					}
				}
			}, true );

			// Handle keydown events for keyboard users
			tab.addEventListener( 'keydown', e => {
				// Get the index of the current tab in the tabs node list
				let index = Array.prototype.indexOf.call( tabs, e.currentTarget );
				// Work out which key the user is pressing and calculate the new tab's index where appropriate
				let dir = e.which === 37 ? index - 1 : e.which === 39 ? index + 1 : e.which === 40 ? 'down' : null;
				if ( dir !== null ) {
					e.preventDefault();
					// If the down key is pressed, move focus to the open panel, otherwise switch to the adjacent tab
					if ( dir === 'down' ) {
						panels[i].focus();
					} else
						if ( tabs[dir] ) {
							const event = new CustomEvent( 'tm__change-tab', {detail: {oldTab: e.currentTarget, newTab: tabs[dir]}} );
							tm.$sidebar.dispatchEvent( event );
						} else {
							void 0;
						}
				}
			} );
		} );

		// Initialize panels
		Array.prototype.forEach.call( panels, ( panel, i ) => {
			panel.setAttribute( 'role', 'tabpanel' );
			panel.setAttribute( 'tabindex', '-1' );
			panel.setAttribute( 'aria-labelledby', tabs[i].id );
			panel.setAttribute( 'aria-hidden', i !== 0 );
			if ( i !== 0 ) {
				panel.setAttribute( 'inert', '' );
			}
		} );
	}

	return {
		getProperty: ( property ) => {
			return tm[property];
		},

		init: () => {
			if ( document.readyState === "interactive" || document.readyState === "complete" ) {
				constructor();
			} else {
				document.addEventListener( "DOMContentLoaded", constructor );
			}
		}
	}
})();

TransitMap.init()
